import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const Nav = () => {
  const location = useLocation();
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const getCurrentDate = () => {
    const today = new Date();
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return today.toLocaleDateString("en-US", options);
  };

  const navItems = [
    { name: "FRONT PAGE", path: "/" },
    { name: "RESOURCES", path: "/resources" },
    { name: "VETERAN'S PAGE", path: "/veterans" },
    { name: "ARTICLES", path: "/articles" },
  ];

  const contactItems = [
    { name: "CONTACT US", path: "/contact" },
    { name: "ABOUT", path: "/about" },
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsContactDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check if any contact-related page is active
  const isContactSectionActive = contactItems.some(
    (item) => location.pathname === item.path
  );
  const BrandHeading = location.pathname === "/" ? "h1" : "div";

  return (
    <div className="w-full bg-gray-900 border-b-4 border-cyan-500 font-serif">
      {/* Newspaper Header */}
      <header className="bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Top bar with date and location */}
          <div className="flex justify-between items-center text-sm mb-4 border-b border-gray-600 pb-2">
            <div className="flex items-center space-x-2 md:space-x-4">
              <span className="font-semibold text-white text-xs md:text-sm">
                {getCurrentDate()}
              </span>
              <span className="text-gray-400 hidden sm:inline">|</span>
              <span className="text-gray-300 text-xs md:text-sm hidden sm:inline">
                Laconia, New Hampshire
              </span>
            </div>
            <div className="flex items-center space-x-2 md:space-x-4">
              <span className="text-white text-xs md:text-sm">Vol. 1</span>
              <span className="text-gray-400">|</span>
              <span className="text-white text-xs md:text-sm">Issue 247</span>
            </div>
          </div>

          {/* Main Newspaper Title */}
          <div className="text-center">
            <Link to="/" className="block">
              <BrandHeading className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-wider mb-2 text-white hover:text-gray-300 transition-colors">
                RECOVERY COMPASS
              </BrandHeading>
            </Link>
            <div className="flex items-center justify-center space-x-2 md:space-x-4 mb-4">
              <div className="h-px bg-cyan-500 w-12 md:w-24"></div>
              <span className="text-cyan-400 font-semibold tracking-widest text-sm md:text-lg">
                NEW HAMPSHIRE
              </span>
              <div className="h-px bg-cyan-500 w-12 md:w-24"></div>
            </div>
            <p className="text-gray-300 italic text-sm md:text-lg px-4">
              "New Hampshire's First Comprehensive SUD & Mental Health Resource Hub"
            </p>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800 text-white border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          {/* Desktop Navigation */}
          <div className="hidden lg:flex justify-center space-x-8 py-3">
            {/* Regular nav items */}
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`hover:text-cyan-400 transition-colors font-semibold px-2 py-1 ${
                  location.pathname === item.path
                    ? "text-cyan-400 border-b-2 border-cyan-400"
                    : "text-white"
                }`}
              >
                {item.name}
              </Link>
            ))}

            {/* Contact dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsContactDropdownOpen(!isContactDropdownOpen)}
                className={`hover:text-cyan-400 transition-colors font-semibold px-2 py-1 flex items-center space-x-1 ${
                  isContactSectionActive
                    ? "text-cyan-400 border-b-2 border-cyan-400"
                    : "text-white"
                }`}
              >
                <span>CONTACT US</span>
                <svg
                  className={`w-4 h-4 transition-transform ${
                    isContactDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* Desktop Dropdown Menu */}
              {isContactDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-gray-800 border-2 border-gray-600 shadow-lg z-50">
                  {contactItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsContactDropdownOpen(false)}
                      className={`block px-4 py-3 text-sm font-semibold border-b border-gray-700 last:border-b-0 transition-colors ${
                        location.pathname === item.path
                          ? "text-cyan-400 bg-gray-700"
                          : "text-white hover:text-cyan-400 hover:bg-gray-700"
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="lg:hidden" ref={mobileMenuRef}>
            {/* Mobile Menu Button */}
            <div className="flex justify-between items-center py-3">
              <span className="text-white font-semibold text-sm">MENU</span>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-white hover:text-cyan-400 transition-colors"
              >
                <svg
                  className={`w-6 h-6 transition-transform ${
                    isMobileMenuOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isMobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>

            {/* Mobile Menu Items */}
            {isMobileMenuOpen && (
              <div className="border-t border-gray-700 py-2">
                {/* Regular nav items */}
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-4 py-3 text-sm font-semibold border-b border-gray-700 transition-colors ${
                      location.pathname === item.path
                        ? "text-cyan-400 bg-gray-700"
                        : "text-white hover:text-cyan-400 hover:bg-gray-700"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}

                {/* Contact items in mobile */}
                <div className="border-t border-gray-600 mt-2 pt-2">
                  {contactItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-4 py-3 text-sm font-semibold border-b border-gray-700 last:border-b-0 transition-colors ${
                        location.pathname === item.path
                          ? "text-cyan-400 bg-gray-700"
                          : "text-white hover:text-cyan-400 hover:bg-gray-700"
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Nav;
